import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { mobileKaiinTorokuService } from '../services/mobileKaiinTorokuService'
import { mobileAuthService } from '../services/mobileAuthService'
import { useApiError } from '../../shared/hooks/useApiError'
import { MessageDialog } from '../../shared/ui'
import { generateDeviceId } from '../../shared/utils/deviceIdGenerator.js'
import toast from 'react-hot-toast'

// CSS-in-JS スタイル
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },
  title: {
    margin: '60px 10px 40px 10px',
    padding: '6px 0 6px 20px',
    background: '#FDEFEF',
    borderLeft: '6px solid #2B2222'
  },
  titleText: {
    fontSize: '14pt',
    fontWeight: 'bold',
    color: '#2B2222'
  },
  inputField: {
    width: '95%',
    display: 'block',
    margin: '10px auto',
    padding: '10px',
    border: '1px solid',
    borderColor: '#ddd',
    borderRadius: '4px',
    fontSize: '16px',
    background: '#fafafa'
  },
  inputFieldFocus: {
    outline: 'none',
    borderColor: '#2B2222',
    background: 'white'
  },
  inputFieldError: {
    borderColor: '#F42222',
    background: '#fff5f5'
  },
  caption: {
    fontSize: '10pt',
    borderLeft: '8px solid #f93535',
    margin: '4px 0 4px 10px',
    padding: '0 10px',
    color: '#2B2222'
  },
  nextButton: {
    width: '90%',
    display: 'block',
    margin: '40px auto',
    padding: '12px 0',
    background: '#262222',
    color: 'white',
    letterSpacing: '6px',
    borderRadius: '24px',
    border: 'none',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  },
  nextButtonHover: {
    background: '#3a3a3a'
  },
  nextButtonDisabled: {
    background: '#999',
    cursor: 'not-allowed'
  },
  errorMessage: {
    color: '#F42222',
    fontSize: '12pt',
    textAlign: 'center',
    margin: '0 10px',
    fontWeight: 'bold'
  },
  message: {
    fontSize: '12pt',
    textAlign: 'center',
    margin: '0 10px',
    color: '#2B2222'
  },
  privacy: {
    textAlign: 'center',
    margin: '20px auto',
    padding: '20px',
    color: '#666',
    fontSize: '12pt',
    lineHeight: 1.5
  },
  footerContents: {
    textAlign: 'center',
    position: 'fixed',
    bottom: 0,
    width: '100%',
    height: '40px',
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: '#2B2222',
    fontSize: '12pt'
  },
  loginLink: {
    color: '#F42222',
    textAlign: 'center',
    fontWeight: 'bold',
    textDecoration: 'none'
  },
  loginLinkHover: {
    textDecoration: 'underline'
  },
  stepContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  passwordContainer: {
    position: 'relative',
    width: '95%',
    margin: '10px auto'
  },
  passwordInput: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    background: '#fafafa',
    paddingRight: '40px' // トグルボタンの幅を考慮
  },
  passwordToggle: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: '#666',
    transition: 'color 0.2s ease'
  },
  passwordToggleHover: {
    color: '#2B2222'
  }
}

const AppKaiinToroku = () => {
  const navigate = useNavigate()
  
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false))
  
  // フォーム状態
  const [currentStep, setCurrentStep] = useState(1) // 現在のステップ
  const [formData, setFormData] = useState({
    mailAddress: '', // メールアドレス
    userName: '', // ユーザー名
    loginPassword1: '', // パスワード
    loginPassword2: '' // 確認用パスワード
  })
  const [errors, setErrors] = useState({}) // エラーメッセージ
  const [isLoading, setIsLoading] = useState(false) // ローディング状態
  const [message, setMessage] = useState('') // メッセージ
  const [showPassword1, setShowPassword1] = useState(false) // パスワード1表示フラグ
  const [showPassword2, setShowPassword2] = useState(false) // パスワード2表示フラグ

  // フォーム操作関数群
  const clearErrors = () => {
    setErrors({})
    setMessage('')
    setShowPassword1(false)
    setShowPassword2(false)
  }

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
    // フィールド入力時にエラーメッセージもクリア
    if (message) {
      setMessage('')
    }
  }

  // ステップ1: メールアドレス入力処理
  const handleMailAddressSubmit = async () => {
    if (!formData.mailAddress.trim()) {
      setErrors({ mailAddress: 'メールアドレスを入力してください。' })
      return
    }

    // メールアドレスの有効性チェック
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(formData.mailAddress)) {
      setErrors({ mailAddress: '有効なメールアドレスを入力してください。' })
      return
    }

    setIsLoading(true)
    try {
      // メールアドレスの重複チェック
      const result = await mobileKaiinTorokuService.getPixUserCount(formData.mailAddress)

      if(result.returnCode !== '200'){
        MessageDialog.show('エラー', 'メールアドレスの重複チェックに失敗しました。', null, 'error');
        return;
      }
      
      if (result.pixUserCount >= 1) {
        setErrors({ mailAddress: 'このメールアドレスは既に登録されています' })
        setMessage('')
      } else {
        setCurrentStep(2)
        clearErrors()
      }
    } catch (error) {
      handleApiError(new Error('メールアドレスの重複チェックに失敗しました。'))
    } finally {
      setIsLoading(false)
    }
  }

  // ステップ2: 名前・パスワード入力処理
  const handleNamePasswordSubmit = async () => {
    const newErrors = {}
    
    if (!formData.userName.trim()) {
      newErrors.userName = '名前を入力してください。'
    }
    if (!formData.loginPassword1) {
      newErrors.loginPassword1 = 'パスワードを入力してください。'
    }
    if (!formData.loginPassword2) {
      newErrors.loginPassword2 = '確認用パスワードを入力してください。'
    }
    if (formData.loginPassword1 !== formData.loginPassword2) {
      newErrors.loginPassword2 = '確認用パスワードが一致しません。'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)
    try {
      // ユーザー登録
      const registResult = await mobileKaiinTorokuService.addPixUser({
        userName: formData.userName,
        loginPassword: formData.loginPassword1,
        mailAddress: formData.mailAddress
      })
      
      if (registResult.returnCode === '200') {
        toast.success('ユーザー登録が完了しました。')
        setCurrentStep(3)
        clearErrors()
      } else {
        setErrors({ general: 'ユーザー登録に失敗しました。' })
      }
    } catch (error) {
      handleApiError(new Error('ユーザー登録に失敗しました。'))
    } finally {
      setIsLoading(false)
    }
  }

  // ステップ3: 登録完了処理
  const handleComplete = async () => {
    setIsLoading(true)
    try {
      let deviceId
      try {
        deviceId = generateDeviceId()
      } catch (deviceError) {
        // フォールバック用のデバイスID
        deviceId = Date.now().toString(16)
      }
      
      const loginResult = await mobileAuthService.login({
        mailAddress: formData.mailAddress,
        password: formData.loginPassword1,
        deviceId: deviceId
      })
      
      if (loginResult && loginResult.successFlg === '1') {
        toast.success('ログインが完了しました。')
        navigate('/appTop')
      } else {
        // ログイン失敗時は自動的にログインページに遷移
        toast.error('ログインに失敗しました。ログイン画面で再度ログインしてください。')
        navigate('/appLogin')
      }
    } catch (error) {
      // エラー時も自動的にログインページに遷移
      toast.error('ログインに失敗しました。ログイン画面で再度ログインしてください。')
      navigate('/appLogin')
    } finally {
      setIsLoading(false)
    }
  }

  // レンダリング関数群
  const renderStep1 = () => (
    <motion.div
      key="step1"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      style={styles.stepContainer}
    >
      <div style={styles.title}>
        <span style={styles.titleText}>メールアドレスの登録</span>
      </div>
      
      <input
        type="text"
        value={formData.mailAddress}
        onChange={(e) => updateFormData('mailAddress', e.target.value)}
        placeholder="メールアドレス"
        style={{
          ...styles.inputField,
          ...(errors.mailAddress ? styles.inputFieldError : {})
        }}
        onKeyPress={(e) => e.key === 'Enter' && handleMailAddressSubmit()}
        onBlur={(e) => {
          // メールアドレスバリデーション
          const email = e.target.value;
          if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
            setErrors({ mailAddress: '有効なメールアドレスを入力してください。' });
          } else if (errors.mailAddress && errors.mailAddress === '有効なメールアドレスを入力してください。') {
            setErrors(prev => ({ ...prev, mailAddress: '' }));
          }
          // フォーカスアウト時のスタイル変更
          e.target.style.borderColor = '#ddd'
          e.target.style.background = '#fafafa'
        }}
        onFocus={(e) => {
          e.target.style.outline = 'none'
          e.target.style.borderColor = '#2B2222'
          e.target.style.background = 'white'
        }}
      />
      
      {errors.mailAddress && (
        <div style={styles.errorMessage}>{errors.mailAddress}</div>
      )}
      
      <button
        onClick={handleMailAddressSubmit}
        disabled={isLoading}
        style={{
          ...styles.nextButton,
          ...(isLoading ? styles.nextButtonDisabled : {})
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.target.style.background = '#3a3a3a'
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.target.style.background = '#262222'
          }
        }}
        aria-busy={isLoading}
        aria-label={isLoading ? '処理中...' : '次へ'}
      >
        {isLoading ? '処理中...' : '次へ'}
      </button>
    </motion.div>
  )

  const renderStep2 = () => (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      style={styles.stepContainer}
    >
      <div style={styles.title}>
        <span style={styles.titleText}>名前とパスワードの登録</span>
      </div>
      <div style={styles.caption}>お名前</div>
      <input
        type="text"
        value={formData.userName}
        onChange={(e) => updateFormData('userName', e.target.value)}
        placeholder="氏名"
        style={{
          ...styles.inputField,
          ...(errors.userName ? styles.inputFieldError : {})
        }}
        onFocus={(e) => {
          e.target.style.outline = 'none'
          e.target.style.borderColor = '#2B2222'
          e.target.style.background = 'white'
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#ddd'
          e.target.style.background = '#fafafa'
        }}
      />
      {errors.userName && (
        <div style={styles.errorMessage}>{errors.userName}</div>
      )}
      <div style={styles.caption}>パスワード</div>
      <div style={styles.passwordContainer}>
        <input
          type={showPassword1 ? 'text' : 'password'}
          value={formData.loginPassword1}
          onChange={(e) => updateFormData('loginPassword1', e.target.value)}
          placeholder="パスワード"
          style={{
            ...styles.passwordInput,
            ...(errors.loginPassword1 ? styles.inputFieldError : {})
          }}
          onFocus={(e) => {
            e.target.style.outline = 'none'
            e.target.style.borderColor = '#2B2222'
            e.target.style.background = 'white'
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#ddd'
            e.target.style.background = '#fafafa'
          }}
        />
        <div
          style={{
            ...styles.passwordToggle,
            ...(showPassword1 ? styles.passwordToggleHover : {})
          }}
          onClick={() => setShowPassword1(!showPassword1)}
          title={showPassword1 ? 'パスワードを隠す' : 'パスワードを表示'}
          role="button"
          tabIndex="-1"
          aria-label={showPassword1 ? 'パスワードを隠す' : 'パスワードを表示'}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setShowPassword1(!showPassword1)
            }
          }}
        >
          {showPassword1 ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          )}
        </div>
      </div>
      {errors.loginPassword1 && (
        <div style={styles.errorMessage}>{errors.loginPassword1}</div>
      )}
      
      <div style={styles.passwordContainer}>
        <input
          type={showPassword2 ? 'text' : 'password'}
          value={formData.loginPassword2}
          onChange={(e) => updateFormData('loginPassword2', e.target.value)}
          placeholder="パスワード(確認)"
          style={{
            ...styles.passwordInput,
            ...(errors.loginPassword2 ? styles.inputFieldError : {})
          }}
          onFocus={(e) => {
            e.target.style.outline = 'none'
            e.target.style.borderColor = '#2B2222'
            e.target.style.background = 'white'
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#ddd'
            e.target.style.background = '#fafafa'
          }}
        />
        <div
          style={{
            ...styles.passwordToggle,
            ...(showPassword2 ? styles.passwordToggleHover : {})
          }}
          onClick={() => setShowPassword2(!showPassword2)}
          title={showPassword2 ? 'パスワードを隠す' : 'パスワードを表示'}
          role="button"
          tabIndex="-1"
          aria-label={showPassword2 ? 'パスワードを隠す' : 'パスワードを表示'}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setShowPassword2(!showPassword2)
            }
          }}
        >
          {showPassword2 ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          )}
        </div>
      </div>
      {errors.loginPassword2 && (
        <div style={styles.errorMessage}>{errors.loginPassword2}</div>
      )}
      
      {errors.general && (
        <div style={styles.errorMessage}>{errors.general}</div>
      )}
      
      <button
        onClick={handleNamePasswordSubmit}
        disabled={isLoading}
        style={{
          ...styles.nextButton,
          ...(isLoading ? styles.nextButtonDisabled : {})
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.target.style.background = '#3a3a3a'
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.target.style.background = '#262222'
          }
        }}
        aria-busy={isLoading}
        aria-label={isLoading ? '処理中...' : '次へ'}
      >
        {isLoading ? '処理中...' : '次へ'}
      </button>
    </motion.div>
  )

  const renderStep3 = () => (
    <motion.div
      key="step3"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      style={styles.stepContainer}
    >
      <div style={styles.title}>
        <span style={styles.titleText}>登録完了</span>
      </div>
      
      <div style={styles.message}>
        <span style={{ fontWeight: 'bold', fontSize: '14pt'}}>{formData.userName}さん</span><br />
        <span style={{ fontWeight: 'bold', fontSize: '14pt' }}>会員登録が完了しました。</span>
      </div>
      
      <div style={styles.privacy}>
        登録することで、利用規約およびプライバシーポリシーに同意するものとします。
      </div>
      
      <button
        onClick={handleComplete}
        disabled={isLoading}
        style={{
          ...styles.nextButton,
          ...(isLoading ? styles.nextButtonDisabled : {})
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.target.style.background = '#3a3a3a'
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.target.style.background = '#262222'
          }
        }}
        aria-busy={isLoading}
        aria-label={isLoading ? '処理中...' : '次へ'}
      >
        {isLoading ? '処理中...' : '次へ'}
      </button>
    </motion.div>
  )

  return (
    <div style={styles.container}>
      <AnimatePresence mode="wait">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </AnimatePresence>
      
      <div style={styles.footerContents}>
        <span>すでにアカウントがある場合</span>
        <a 
          href="/appLogin" 
          style={styles.loginLink}
          onMouseEnter={(e) => {
            e.target.style.textDecoration = 'underline'
          }}
          onMouseLeave={(e) => {
            e.target.style.textDecoration = 'none'
          }}
        >
          ログイン
        </a>
      </div>

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />
    </div>
  )
}

export default AppKaiinToroku
